<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import TemplateCard from './TemplateCard.vue';

const store = useStore();
const getters = useStoreGetters();
const route = useRoute();
const { t } = useI18n();

const promptContent = ref('');
const minLength = 10;
const showDeleteConfirmationPopup = ref(false);

const prompt = computed(() => getters['customPrompt/getPrompt'].value);
const templates = computed(() => getters['customPrompt/getTemplates'].value);
const uiFlags = computed(() => getters['customPrompt/getUIFlags'].value);
const validation = computed(() => getters['customPrompt/getValidation'].value);

const hasApiKey = computed(() => prompt.value.has_api_key !== false);
const accountId = computed(() => route.params.accountId);
const agentBotSettingsUrl = computed(
  () => `/app/accounts/${accountId.value}/settings/integrations/agent_bots`
);

const maxLength = computed(() => prompt.value.max_length || 2000);
const characterCount = computed(() => promptContent.value.length);
const isContentTooShort = computed(
  () => promptContent.value.length > 0 && promptContent.value.length < minLength
);
const isContentTooLong = computed(
  () => promptContent.value.length > maxLength.value
);
const canSubmit = computed(
  () =>
    hasApiKey.value &&
    promptContent.value.length >= minLength &&
    !isContentTooLong.value &&
    !uiFlags.value.isSaving &&
    !uiFlags.value.isValidating
);
const canDelete = computed(() => !!prompt.value.id && !uiFlags.value.isSaving);

const statusType = computed(() => {
  if (isContentTooShort.value) return 'warning';
  if (isContentTooLong.value) return 'error';
  if (validation.value.valid === true) return 'success';
  if (validation.value.valid === false) return 'error';
  return 'info';
});

const statusMessage = computed(() => {
  if (isContentTooShort.value) {
    return t('CUSTOM_PROMPT.VALIDATION.MIN_LENGTH', { minLength });
  }
  if (isContentTooLong.value) {
    return t('CUSTOM_PROMPT.VALIDATION.MAX_LENGTH', {
      maxLength: maxLength.value,
    });
  }
  if (validation.value.message) {
    return validation.value.message;
  }
  return '';
});

const onTemplateClick = template => {
  promptContent.value = template.content;
  store.dispatch('customPrompt/clearValidation');
};

const onDelete = async () => {
  try {
    await store.dispatch('customPrompt/deletePrompt');
    promptContent.value = '';
    useAlert(t('CUSTOM_PROMPT.API.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('CUSTOM_PROMPT.API.DELETE_ERROR'));
  } finally {
    showDeleteConfirmationPopup.value = false;
  }
};

const onSubmit = async () => {
  if (!canSubmit.value) return;

  try {
    // First validate with chatbackend
    await store.dispatch('customPrompt/validatePrompt', promptContent.value);

    // If validation passed, save
    await store.dispatch('customPrompt/savePrompt', {
      content: promptContent.value,
      active: true,
    });

    useAlert(t('CUSTOM_PROMPT.API.SAVE_SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.message || t('CUSTOM_PROMPT.API.SAVE_ERROR');
    useAlert(errorMessage);
  }
};

watch(
  () => prompt.value.content,
  newContent => {
    if (newContent && !promptContent.value) {
      promptContent.value = newContent;
    }
  },
  { immediate: true }
);

onBeforeMount(() => {
  store.dispatch('customPrompt/fetchPrompt');
  store.dispatch('customPrompt/fetchTemplates');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('CUSTOM_PROMPT.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('CUSTOM_PROMPT.HEADER')"
        :description="$t('CUSTOM_PROMPT.DESCRIPTION')"
        feature-name="custom_prompt"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-8">
        <!-- No API Key warning banner -->
        <div
          v-if="!hasApiKey"
          class="flex flex-col items-center gap-4 p-8 rounded-xl border border-yellow-300 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20"
        >
          <fluent-icon icon="warning" size="32" class="text-yellow-600 dark:text-yellow-400" />
          <h3 class="text-lg font-semibold text-yellow-800 dark:text-yellow-200">
            {{ $t('CUSTOM_PROMPT.NO_API_KEY.TITLE') }}
          </h3>
          <p class="text-sm text-yellow-700 dark:text-yellow-300 text-center max-w-lg">
            {{ $t('CUSTOM_PROMPT.NO_API_KEY.MESSAGE') }}
          </p>
          <a
            :href="agentBotSettingsUrl"
            class="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-600 text-white hover:bg-yellow-700 transition-colors text-sm font-medium"
          >
            <fluent-icon icon="bot" size="16" />
            {{ $t('CUSTOM_PROMPT.NO_API_KEY.BUTTON') }}
          </a>
        </div>

        <!-- Status message -->
        <div
          v-if="statusMessage"
          class="flex items-center gap-2 px-4 py-2 rounded-lg text-sm"
          :class="{
            'bg-yellow-50 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200':
              statusType === 'warning',
            'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-200':
              statusType === 'error',
            'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-200':
              statusType === 'success',
            'bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200':
              statusType === 'info',
          }"
        >
          <fluent-icon
            :icon="
              statusType === 'warning'
                ? 'warning'
                : statusType === 'error'
                  ? 'dismiss-circle'
                  : statusType === 'success'
                    ? 'checkmark-circle'
                    : 'info'
            "
            size="16"
          />
          <span>{{ statusMessage }}</span>
        </div>

        <!-- Prompt textarea -->
        <div
          class="relative rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-25 dark:bg-slate-800 shadow-sm"
        >
          <textarea
            v-model="promptContent"
            class="w-full min-h-[200px] p-6 bg-transparent border-0 resize-y text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-0 text-base leading-relaxed"
            :class="{ 'opacity-50 cursor-not-allowed': !hasApiKey }"
            :placeholder="$t('CUSTOM_PROMPT.PLACEHOLDER')"
            :maxlength="maxLength + 100"
            :disabled="!hasApiKey"
            @input="store.dispatch('customPrompt/clearValidation')"
          />
          <div
            class="flex items-center justify-between px-6 py-3 border-t border-slate-200 dark:border-slate-700"
          >
            <span
              class="text-xs"
              :class="{
                'text-red-500': isContentTooLong,
                'text-slate-400 dark:text-slate-500': !isContentTooLong,
              }"
            >
              {{ characterCount }} / {{ maxLength }}
            </span>
            <div class="flex items-center gap-2">
              <woot-button
                v-if="canDelete"
                variant="clear"
                color-scheme="alert"
                :is-disabled="uiFlags.isSaving || uiFlags.isValidating"
                class="rounded-lg"
                @click="showDeleteConfirmationPopup = true"
              >
                {{ $t('CUSTOM_PROMPT.DELETE.CONFIRM.YES') }}
              </woot-button>
              <woot-button
                :is-loading="uiFlags.isSaving || uiFlags.isValidating"
                :is-disabled="!canSubmit"
                class="rounded-lg"
                @click="onSubmit"
              >
                {{ $t('CUSTOM_PROMPT.SUBMIT') }}
              </woot-button>
            </div>
          </div>
        </div>

        <!-- Templates section -->
        <div v-if="templates.length" class="flex flex-col gap-4">
          <h3 class="text-lg font-semibold text-slate-800 dark:text-slate-100">
            {{ $t('CUSTOM_PROMPT.TEMPLATES.HEADER') }}
          </h3>
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <TemplateCard
              v-for="template in templates"
              :key="template.id"
              :template="template"
              @click="onTemplateClick(template)"
            />
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>

  <woot-delete-modal
    v-model:show="showDeleteConfirmationPopup"
    :on-close="() => (showDeleteConfirmationPopup = false)"
    :on-confirm="onDelete"
    :title="$t('CUSTOM_PROMPT.DELETE.CONFIRM.TITLE')"
    :message="$t('CUSTOM_PROMPT.DELETE.CONFIRM.MESSAGE')"
    :confirm-text="$t('CUSTOM_PROMPT.DELETE.CONFIRM.YES')"
    :reject-text="$t('CUSTOM_PROMPT.DELETE.CONFIRM.NO')"
  />
</template>
